/* eslint-disable import/prefer-default-export */
/**
 * Sentry Error Filters (Shared between client and server)
 *
 * This module contains handlers that filter out expected errors from being sent to Sentry.
 * Each handler returns `true` if the error should be filtered out (not sent to Sentry).
 *
 * HOW TO ADD A NEW FILTER:
 * 1. Create a new function: `shouldFilterYourError(event, hint)`
 * 2. Implement your logic (return true to filter out, false to send to Sentry)
 * 3. Add your function to the `filters` array in `shouldFilterError()`
 *
 * EXAMPLE:
 * ```javascript
 * const shouldFilterMyError = (event, hint) => {
 *   if (!hint || !hint.originalException) {
 *     return false;
 *   }
 *   const error = hint.originalException;
 *   return error?.status === 404 && error?.message?.includes('not found');
 * };
 * ```
 */

/**
 * Filter timeslot 400 errors - these are expected for listings without availability configured
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Sentry hint object with originalException
 * @returns {boolean} - true if error should be filtered out
 */
const shouldFilterTimeslot400Error = (event, hint) => {
  // Guard check: ensure hint and originalException exist
  if (!hint || !hint.originalException) {
    return false;
  }

  const error = hint.originalException;

  // Only check 400 status errors
  if (!error || error.status !== 400) {
    return false;
  }

  // Check if the error came from timeslot-related code
  const stackTrace = event.exception?.values?.[0]?.stacktrace?.frames || [];
  const hasTimeslotInStack = stackTrace.some(
    frame =>
      frame.filename?.includes('timeslots') ||
      frame.function?.includes('timeSlot') ||
      frame.function?.includes('fetchTimeSlots')
  );

  // Also check the breadcrumbs for timeslots API calls
  const breadcrumbs = event.breadcrumbs || [];
  const hasTimeslotInBreadcrumbs = breadcrumbs.some(
    breadcrumb => breadcrumb.category === 'xhr' && breadcrumb.data?.url?.includes('/timeslots/')
  );

  return hasTimeslotInStack || hasTimeslotInBreadcrumbs;
};

/**
 * Filter 409 email-taken errors - these occur when users try to sign up with an already registered email
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Sentry hint object with originalException
 * @returns {boolean} - true if error should be filtered out
 */
const shouldFilterEmailTaken409Error = (event, hint) => {
  // Guard check: ensure hint and originalException exist
  if (!hint || !hint.originalException) {
    return false;
  }

  const error = hint.originalException;

  // Only check 409 status errors
  if (!error || error.status !== 409) {
    return false;
  }

  // Check if this is an email-taken error
  const errorMessage = error.message?.toLowerCase() || '';
  const hasEmailTaken =
    errorMessage.includes('email-taken') ||
    errorMessage.includes('email_taken') ||
    errorMessage.includes('email already');

  return hasEmailTaken;
};

/**
 * Filter 401 errors that are handled by automatic token refresh/retry logic
 * These are expected when tokens expire and the app automatically refreshes them
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Sentry hint object with originalException
 * @returns {boolean} - true if error should be filtered out
 */
const shouldFilter401WithRetry = (event, hint) => {
  // Guard check: ensure hint and originalException exist
  if (!hint || !hint.originalException) {
    return false;
  }

  const error = hint.originalException;
  const status = error?.status || error?.response?.status;

  // Only check 401 status errors
  if (!error || status !== 401) {
    return false;
  }

  // Filter handled 401s regardless of exact error shape/message.
  const errorMessage = (error?.message || '').toLowerCase();
  if (
    errorMessage.includes('401') ||
    errorMessage.includes('unauthorized') ||
    error?.statusText === 'Unauthorized'
  ) {
    return true;
  }

  // Check Sentry exception message as fallback.
  const exceptionMessage = (
    event.exception?.values?.[0]?.value ||
    event.message ||
    ''
  ).toLowerCase();
  if (exceptionMessage.includes('401') || exceptionMessage.includes('unauthorized')) {
    return true;
  }

  return false;
};

/**
 * Filter 403 permission errors - these are expected when users don't have access to resources
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Sentry hint object with originalException
 * @returns {boolean} - true if error should be filtered out
 */
const shouldFilter403PermissionError = (event, hint) => {
  // Guard check: ensure hint and originalException exist
  if (!hint || !hint.originalException) {
    return false;
  }

  const error = hint.originalException;

  // Filter out all 403 status errors (permission/forbidden errors)
  if (error && error.status === 403) {
    return true;
  }

  // Also check the error message for 403 status codes
  const errorMessage = error?.message || '';
  if (errorMessage.includes('status code 403') || errorMessage.includes('403')) {
    return true;
  }

  return false;
};

/**
 * Filter 404 not found errors - these are expected when resources don't exist
 * (e.g., deleted users, removed listings, missing profile images)
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Sentry hint object with originalException
 * @returns {boolean} - true if error should be filtered out
 */
const shouldFilter404NotFoundError = (event, hint) => {
  // Guard check: ensure hint and originalException exist
  if (!hint || !hint.originalException) {
    return false;
  }

  const error = hint.originalException;

  // Filter out all 404 status errors (not found errors)
  if (error && error.status === 404) {
    return true;
  }

  // Also check the error message for 404 status codes (using specific patterns to avoid false positives)
  const errorMessage = error?.message || '';
  if (
    /\bstatus code 404\b/i.test(errorMessage) ||
    /Request failed with status code 404\b/i.test(errorMessage) ||
    /\b404\b.*status/i.test(errorMessage) ||
    /status.*\b404\b/i.test(errorMessage)
  ) {
    return true;
  }

  // Check the event exception values
  const exceptionMessage = event.exception?.values?.[0]?.value || event.message || '';
  if (
    /\bstatus code 404\b/i.test(exceptionMessage) ||
    /Request failed with status code 404\b/i.test(exceptionMessage) ||
    /\b404\b.*status/i.test(exceptionMessage) ||
    /status.*\b404\b/i.test(exceptionMessage)
  ) {
    return true;
  }

  return false;
};

/**
 * Filter JSON.stringify cyclic structure errors - these occur when trying to serialize
 * objects with circular references, often from third-party libraries or analytics
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Sentry hint object with originalException
 * @returns {boolean} - true if error should be filtered out
 */
const shouldFilterCyclicStructureError = (event, hint) => {
  // Guard check: ensure hint and originalException exist
  if (!hint || !hint.originalException) {
    return false;
  }

  const error = hint.originalException;

  // Check error message for cyclic structure errors
  const errorMessage = error?.message || '';
  if (
    errorMessage.includes('JSON.stringify cannot serialize cyclic structures') ||
    errorMessage.includes('cyclic structure') ||
    errorMessage.includes('circular structure')
  ) {
    return true;
  }

  // Also check the event message/exception values
  const exceptionMessage = event.exception?.values?.[0]?.value || event.message || '';
  if (
    exceptionMessage.includes('JSON.stringify cannot serialize cyclic structures') ||
    exceptionMessage.includes('cyclic structure') ||
    exceptionMessage.includes('circular structure')
  ) {
    return true;
  }

  return false;
};

/**
 * Filter create-user-with-idp-failed errors - these occur when identity provider
 * user creation fails, which is often expected behavior (e.g., user already exists)
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Sentry hint object with originalException
 * @returns {boolean} - true if error should be filtered out
 */
const shouldFilterCreateUserWithIdpFailedError = (event, hint) => {
  // Check if the error code tag matches
  const errorCode = event.tags?.code;
  if (errorCode === 'create-user-with-idp-failed') {
    return true;
  }

  // Also filter create-user-with-idp endpoint 409s tagged as local API failures.
  const status =
    hint?.originalException?.status ||
    hint?.originalException?.statusCode ||
    hint?.originalException?.response?.status;
  const transaction = event.transaction || '';
  if (
    transaction.includes('create-user-with-idp') &&
    errorCode === 'local-api-request-failed' &&
    status === 409
  ) {
    return true;
  }

  return false;
};

/**
 * Filter AxiosError: Request aborted errors - these occur when requests are cancelled
 * due to navigation, component unmounting, or browser behavior. These are expected
 * and not actionable errors.
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Sentry hint object with originalException
 * @returns {boolean} - true if error should be filtered out
 */
const shouldFilterAxiosRequestAbortedError = (event, hint) => {
  // Guard check: ensure hint and originalException exist
  if (!hint || !hint.originalException) {
    return false;
  }

  const error = hint.originalException;
  const errorMessage = (error?.message || '').toLowerCase();
  const rawCode = error?.code;
  if (rawCode != null && typeof rawCode !== 'string') {
    // eslint-disable-next-line no-console
    console.warn('[sentryFilters] non-string error.code detected', {
      codeType: typeof rawCode,
      codeValue: rawCode,
      errorName: error?.name,
      errorMessage: error?.message,
      errorStatus: error?.status,
      errorConstructor: error?.constructor?.name,
    });
  }
  const errorCode = String(rawCode || '').toUpperCase();

  // Axios may mark canceled/aborted requests with specific error codes.
  // Treat ERR_CANCELED as an explicit cancelation signal.
  if (errorCode === 'ERR_CANCELED') {
    return true;
  }

  // ECONNABORTED is ambiguous (can be cancel OR timeout). Only filter when the
  // message clearly indicates an abort/cancel, not a timeout.
  if (
    errorCode === 'ECONNABORTED' &&
    (errorMessage.includes('abort') ||
      errorMessage.includes('aborted') ||
      errorMessage.includes('canceled') ||
      errorMessage.includes('cancelled'))
  ) {
    return true;
  }
  // Axios 1.x uses CanceledError for canceled requests.
  if (error?.name === 'CanceledError' || error?.constructor?.name === 'CanceledError') {
    return true;
  }

  // Match plain errors too (e.g. Error('Request aborted')).
  if (errorMessage === 'request aborted' || errorMessage === 'canceled') {
    return true;
  }

  // Also check the event message/exception values.
  const exceptionMessage = (
    event.exception?.values?.[0]?.value ||
    event.message ||
    ''
  ).toLowerCase();
  if (
    exceptionMessage.includes('request aborted') ||
    exceptionMessage.includes('axioserror: request aborted') ||
    exceptionMessage.includes('canceled')
  ) {
    return true;
  }

  return false;
};

/**
 * Example: Filter expected checkout errors
 * Uncomment and modify as needed
 *
 * const shouldFilterExpectedCheckoutError = (event, hint) => {
 *   if (!hint || !hint.originalException) {
 *     return false;
 *   }
 *
 *   const error = hint.originalException;
 *
 *   if (error?.status === 409 && error?.message?.includes('already processed')) {
 *     return true; // Filter out duplicate checkout attempts
 *   }
 *
 *   return false;
 * };
 */

/**
 * Example: Filter network timeout errors
 * Uncomment and modify as needed
 *
 * const shouldFilterNetworkTimeouts = (event, hint) => {
 *   if (!hint || !hint.originalException) {
 *     return false;
 *   }
 *
 *   const error = hint.originalException;
 *
 *   if (error?.message?.includes('timeout') || error?.code === 'ETIMEDOUT') {
 *     return true; // Filter out network timeouts
 *   }
 *
 *   return false;
 * };
 */

/**
 * Safely stringify an object, handling circular references and complex objects.
 * If serialization fails, returns a simple fallback representation.
 *
 * @param {*} obj - The object to sanitize
 * @returns {*} - A safe version of the object, or a fallback if serialization fails
 */
const sanitizeForSentry = obj => {
  try {
    // Try simple JSON stringify/parse - this handles most cases
    return JSON.parse(JSON.stringify(obj));
  } catch (e) {
    // If that fails (circular refs, complex objects, etc.), return a simple fallback
    try {
      // Try to at least extract basic error info if it's an error object
      if (obj instanceof Error) {
        return {
          name: obj.name,
          message: obj.message,
          stack: obj.stack,
        };
      }

      // For other objects, return a basic representation
      return {
        type: typeof obj,
        constructor: obj?.constructor?.name || 'Unknown',
        stringified: String(obj),
      };
    } catch (fallbackError) {
      // Ultimate fallback
      return { error: 'Could not sanitize data' };
    }
  }
};

/**
 * Master filter function that runs all error filters
 * Returns true if the error should be filtered out (not sent to Sentry)
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Sentry hint object
 * @returns {boolean} - true if error should be filtered out
 */
const shouldFilterError = (event, hint) => {
  // Guard check: ensure hint exists before passing to filters
  if (!hint) {
    return false;
  }

  const filters = [
    shouldFilterTimeslot400Error,
    shouldFilterEmailTaken409Error,
    shouldFilter401WithRetry,
    shouldFilter403PermissionError,
    shouldFilter404NotFoundError,
    shouldFilterCyclicStructureError,
    shouldFilterCreateUserWithIdpFailedError,
    shouldFilterAxiosRequestAbortedError,
    // Add more filters here as needed
    // shouldFilterExpectedCheckoutError,
  ];

  // If any filter returns true, filter out the error
  return filters.some(filter => filter(event, hint));
};

module.exports = { shouldFilterError, sanitizeForSentry };
